import { api } from './api';
import { Entry, ReferenceEntry, RepositoryByIdResponse } from '../types';
import { entriesAddMany } from '../containers/contentSlice';
import { GetRepositoryEntriesResponseDto, RepositoryEntriesRowDto } from './repositoryEntries';

export type LoadReferencesAtLocationRepositoryBlockDto = Pick<
	RepositoryByIdResponse,
	'repository' | 'propertyDefinitions'
> & {
	repositoryEntries: RepositoryEntriesRowDto[];
};

export interface LoadReferencesAtLocationResponseDto {
	repositories: LoadReferencesAtLocationRepositoryBlockDto[];
}

export const referencesApi = api.injectEndpoints({
	endpoints: (builder) => ({
		loadReferencesAtLocation: builder.query<LoadReferencesAtLocationResponseDto, string>({
			query: (locationId: string) => ({
				url: 'references/load',
				params: {
					locationId,
				},
				method: 'GET',
			}),
			async onQueryStarted(_locationId, { dispatch, queryFulfilled }) {
				try {
					const { data } = await queryFulfilled;

					for (const block of data.repositories) {
						const repositoryId = block.repository.id;
						const seededRepositoryByIdResponse: RepositoryByIdResponse = {
							repository: block.repository as RepositoryByIdResponse['repository'],
							propertyDefinitions: block.propertyDefinitions as RepositoryByIdResponse['propertyDefinitions'],
						};
						dispatch(
							(api.util.upsertQueryData as (
								endpointName: 'getRepositoryById',
								arg: string,
								value: RepositoryByIdResponse,
							) => ReturnType<typeof api.util.upsertQueryData>)(
								'getRepositoryById',
								repositoryId,
								seededRepositoryByIdResponse,
							),
						);

						for (const row of block.repositoryEntries) {
							dispatch(
								(api.util.upsertQueryData as (
									endpointName: 'loadRepositoryEntry3',
									arg: string,
									value: GetRepositoryEntriesResponseDto,
								) => ReturnType<typeof api.util.upsertQueryData>)(
									'loadRepositoryEntry3',
									row.id,
									{
										repositoryId,
										title: block.repository.title ?? '',
										singular: block.repository.singular ?? '',
										rowCount: 1,
										returnedRows: 1,
										hasMore: false,
										rows: [row],
									},
								),
							);
						}
					}
				} catch {
					// ignore warmup failures; regular per-card queries still work
				}
			},
			providesTags: (_result, _error, locationId) => [
				{ type: 'References' as const, id: locationId },
			],
			extraOptions: { maxRetries: 0 },
		}),
		createReferences: builder.mutation<Entry[], ReferenceEntry[]>({
			query: (references: ReferenceEntry[]) => ({
				url: 'references',
				method: 'POST',
				body: {
					references: references.map((r) => ({
						type: r.type,
						id: r.id,
						parentId: r.parentId,
						repositoryId: r.repositoryId,
						repositoryEntryId: r.repositoryEntryId,
						...(r.xy ? { xy: r.xy } : {}),
						...(r.anchorId != null ? { anchorId: r.anchorId } : {}),
					})),
				},
			}),
			async onQueryStarted(references: ReferenceEntry[], { dispatch }: any) {
				dispatch(entriesAddMany(references));
			},
			// invalidatesTags: (_result, _error, references) => {
			// 	const parentIds = new Set<string>();
			// 	for (const r of references) {
			// 		parentIds.add(String(r.parentId ?? ''));
			// 	}
			// 	return Array.from(parentIds).map((id) => ({
			// 		type: 'References' as const,
			// 		id,
			// 	}));
			// },
			extraOptions: { maxRetries: 0 },
		}),
	}),
});

export const {
	useLoadReferencesAtLocationQuery,
	useCreateReferencesMutation,
} = referencesApi;
